<template>
  <div class="notifications-wrapper">
    <button
      class="notifications-trigger"
      @click="toggleOpen"
      :aria-label="t('notifications.open', 'Open notifications')"
    >
      <span class="icon-bell-wrapper">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        <span
          v-if="unreadCount > 0"
          class="badge"
        >
          {{ unreadCount }}
        </span>
      </span>
    </button>

    <div
      v-if="isOpen"
      class="notifications-dropdown"
      :class="{ 'sidebar': isSidebar }"
    >
      <div class="dropdown-header">
        <span class="title">{{ t('notifications.title', 'Notifications') }}</span>
        <div class="header-actions">
          <button
            class="icon-btn"
            @click="reloadNotifications"
            :title="t('notifications.reload', 'Reload notifications')"
            :aria-label="t('notifications.reload', 'Reload notifications')"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
              <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
            </svg>
          </button>
          <div class="filters">
            <button
              class="filter-btn"
              :class="{ active: !showAll }"
              @click="setShowAll(false)"
            >
              {{ t('notifications.unread', 'Unread') }}
            </button>
            <button
              class="filter-btn"
              :class="{ active: showAll }"
              @click="setShowAll(true)"
            >
              {{ t('notifications.all', 'All') }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="loading && notifications.length === 0"
        class="dropdown-body loading"
      >
        {{ t('notifications.loading', 'Loading notifications...') }}
      </div>
      <div
        v-else-if="error && notifications.length === 0"
        class="dropdown-body error"
      >
        {{ error }}
      </div>
      <div
        v-else-if="notifications.length === 0"
        class="dropdown-body empty"
      >
        {{ t('notifications.empty', 'No notifications') }}
      </div>
      <ul
        v-if="notifications.length > 0"
        class="dropdown-body notifications-list"
      >
        <li
          v-for="item in notifications"
          :key="item.id"
          class="notification-item"
          :class="{ 'notification-read': isRead(item) }"
        >
          <div class="notification-main">
            <div class="notification-title">
              <div class="notification-icon-wrapper">
                <i
                  :class="getNotificationIcon(item.type)"
                  class="notification-icon"
                />
              </div>
              <span 
                class="notification-title-text"
                @click="openNotificationDialog(item)"
              >{{ getNotificationTitle(item) }}</span>
              <button
                v-if="!isRead(item)"
                class="action-icon-btn"
                :aria-label="t('notifications.markAsRead', 'Mark as read')"
                @click="markAsRead(item)"
                title="Mark as read"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </button>
              <button
                v-else
                class="action-icon-btn"
                :aria-label="t('notifications.markAsUnread', 'Mark as unread')"
                @click="markAsUnread(item)"
                title="Mark as unread"
              >
                <svg 
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="m12.317 2.4008 9.6826 6.0992v12c0 1.1-0.9 2-2 2h-16c-1.1 0-2-0.9-2-2v-12z" />
                  <polyline
                    transform="translate(0 2.5)"
                    points="22 6 12 13 2 6"
                  />
                </svg>                
              </button>
            </div>
            <div class="notification-meta">
              <!-- <span class="notification-type">{{ item.type }}</span> -->
              <!-- <span class="notification-id">{{ item.id }}</span> -->
              <span class="notification-date">
                {{ formatDate(item.created_at) }}
              </span>
            </div>
          </div>
        </li>
      </ul>
      <div
        class="dropdown-footer"
      >
        <router-link
          :to="{ path: '/notifications', query: { filter: showAll ? 'all' : 'unread' } }"
          class="view-all-link"
        >
          {{ t('notifications.viewAll', 'View all notifications') }}
        </router-link>
      </div>
    </div>

    <!-- Notification Dialog -->
    <NotificationMessageDialog
      :notification="selectedNotification"
      :r7e-app="r7eApp"
      :show-debug-data="r7eApp?.isUIDebugEnabled() || false"
      @close="closeNotificationDialog"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getNotificationIcon } from '../services/notificationIcons.js';
import NotificationMessageDialog from './NotificationMessageDialog.vue';

const props = defineProps({
  r7eApp: {
    type: Object,
    required: true
  },
  isSidebar: {
    type: Boolean,
    default: false
  }
});

const { t } = useI18n();

const isOpen = ref(false);
const showAll = ref(false);
const notifications = ref([]);
const loading = ref(false);
const error = ref(null);
const selectedNotification = ref(null);
const count = ref(0);
const totalCount = ref(0);
const currentOffset = ref(0);
const pageSize = ref(25);

const fetchNotifications = async (append = false) => {
  loading.value = true;
  error.value = null;
  try {
    const include = showAll.value ? 'all' : '';
    const offset = append ? currentOffset.value : 0;
    const res = await props.r7eApp.getNotificationMessages(offset, pageSize.value, include);
    if (res && res.status === 'success' && Array.isArray(res.items)) {
      if (append) {
        notifications.value = [...notifications.value, ...res.items];
      } else {
        notifications.value = res.items;
      }
      // Store pagination info
      count.value = res.count || res.items.length;
      totalCount.value = res.total_count || res.items.length;
      currentOffset.value = append ? currentOffset.value + res.items.length : res.items.length;
    } else {
      if (!append) {
        notifications.value = [];
      }
      count.value = 0;
      totalCount.value = 0;
      currentOffset.value = 0;
    }
  } catch (e) {
    console.error('Error fetching notifications', e);
    error.value = t('notifications.error', 'Unable to load notifications');
    if (!append) {
      notifications.value = [];
    }
  } finally {
    loading.value = false;
  }
};

const unreadCount = computed(() => {
  return notifications.value.filter(n => !isRead(n)).length;
});

// const hasMore = computed(() => {
//   return currentOffset.value < totalCount.value;
// });

const isRead = (item) => {
  // Backend uses status to toggle read/unread via markNotificationAsRead/Unread
  // Treat "read" as read, everything else as unread

  if (item?.read_at !== undefined && item?.read_at !== null) {

    return true;
  } else {
    return false;
  }
};

const markAsRead = async (item) => {
  try {
    await props.r7eApp.markNotificationAsRead(item.id);
    await fetchNotifications();
  } catch (e) {
    console.error('Error marking notification as read', e);
  }
};

const markAsUnread = async (item) => {
  try {
    await props.r7eApp.markNotificationAsUnread(item.id);
    await fetchNotifications();
  } catch (e) {
    console.error('Error marking notification as unread', e);
  }
};

const reloadNotifications = async () => {
  try {
    currentOffset.value = 0;
    await fetchNotifications();
  } catch (e) {
    console.error('Error reloading notifications', e);
  }
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

// const getNotificationLink = (item) => {
//   // Try common locations for URLs in notification payloads
//   if (item.data && item.data.claim && item.data.claim.url) {
//     return item.data.claim.url;
//   }
//   if (item.data && item.data.url) {
//     return item.data.url;
//   }
//   return null;
// };

const getNotificationTitle = (item) => {
  let key = '';
  if (item.title) {
    key = item.title;
  } else if (item.type) {
    key = item.type;
  }
  const t_key = `notifications.types.${key}`;
  const translated = t(t_key);
  if (translated !== t_key) {
    return translated;
  }
  return key;
  // return item.type || t('notifications.unknownType', 'Notification');
};


const setShowAll = async (value) => {
  if (showAll.value === value) return;
  showAll.value = value;
  currentOffset.value = 0;
  await fetchNotifications();
};


const toggleOpen = async () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value && notifications.value.length === 0 && !loading.value) {
    await fetchNotifications();
  }
};

const handleDocumentClick = (event) => {
  const el = event.target.closest('.notifications-wrapper');
  if (!el) {
    isOpen.value = false;
  }
};

const openNotificationDialog = async (item) => {
  selectedNotification.value = item;
  // Mark as read if not already read
  if (!isRead(item)) {
    await markAsRead(item);
    // Update selectedNotification to point to the refreshed item
    const updatedItem = notifications.value.find(n => n.id === item.id);
    if (updatedItem) {
      selectedNotification.value = updatedItem;
    }
  }
};

const closeNotificationDialog = () => {
  selectedNotification.value = null;
};


onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  // Preload unread notifications quietly
  fetchNotifications();
});

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick);
});
</script>

<style scoped>
.notifications-wrapper {
  position: relative;
  display: inline-block;
}

.notifications-trigger {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-navbar-text);
  padding: 8px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.notifications-trigger:hover {
  color: var(--color-green);
}

.icon-bell-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.badge {
  position: absolute;
  top: -4px;
  right: -6px;
  background-color: var(--color-required-bg, #e74c3c);
  color: var(--color-required-text, #fff);
  border-radius: 999px;
  padding: 0 5px;
  font-size: 0.65rem;
  min-width: 14px;
  text-align: center;
}

.notifications-dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 8px;
  background-color: color-mix(in srgb, var(--color-navbar-bg), black 10%);
  min-width: 280px;
  max-width: 360px;
  max-height: 420px;
  border-radius: 4px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  border: 1px solid color-mix(in srgb, var(--color-border), transparent 50%);
  z-index: 50;
  display: flex;
  flex-direction: column;
}
.notifications-dropdown.sidebar {
  right: auto !important;
}

.dropdown-header {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.dropdown-header .title {
  font-weight: 600;
  font-size: 0.9rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-navbar-text);
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s, color 0.2s;
}

.icon-btn:hover {
  opacity: 1;
  color: var(--color-green);
}

.icon-btn:focus {
  outline: 1px solid var(--color-green);
  outline-offset: 2px;
  border-radius: 2px;
}

.filters {
  display: inline-flex;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.filter-btn {
  border: none;
  background: transparent;
  color: var(--color-navbar-text);
  padding: 4px 8px;
  font-size: 0.75rem;
  cursor: pointer;
}

.filter-btn.active {
  background-color: var(--color-green);
  color: var(--color-navbar-text);
}

.dropdown-body {
  padding: 10px 12px;
  font-size: 0.8rem;
}

.dropdown-body.loading,
.dropdown-body.error,
.dropdown-body.empty {
  color: color-mix(in srgb, var(--color-navbar-text), transparent 20%);
}

.notifications-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 360px;
  overflow-y: auto;
}

.notification-item {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.notification-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: 500;
  font-size: 0.85rem;
}

.notification-icon-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.notification-icon {
  font-size: 0.9rem;
  color: var(--color-green);
  opacity: 0.8;
}

.notification-title-text {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  transition: opacity 0.2s;
}

.notification-title-text:hover {
  opacity: 0.8;
}

.notification-meta {
  display: flex;
  justify-content: flex-end;
  font-size: 0.7rem;
  color: color-mix(in srgb, var(--color-navbar-text), transparent 30%);
}

.notification-date {
  text-align: right;
}

.notification-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.action-link {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-green);
  font-size: 0.75rem;
  padding: 0;
}

.action-link.primary-link {
  text-decoration: none;
}

.action-icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-green);
  padding: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.action-icon-btn:hover {
  opacity: 1;
}

.action-icon-btn:focus {
  outline: 1px solid var(--color-green);
  outline-offset: 2px;
  border-radius: 2px;
}

.dropdown-footer {
  padding: 10px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.view-all-link {
  background: none;
  border: 1px solid var(--color-green);
  color: var(--color-green);
  padding: 6px 12px;
  font-size: 0.75rem;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.2s;
  display: inline-block;
  text-align: center;
}

.view-all-link:hover {
  background-color: var(--color-green);
  color: var(--color-navbar-text);
}

.notification-read .notification-title {
  opacity: 0.7;
  font-weight: 400;
}

.notification-read .badge {
  display: none;
}


@media (max-width: 900px) {
  .notifications-dropdown {
    right: 0;
    left: auto;
  }

  .dropdown-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    justify-content: space-between;
  }

  .mark-all-read-btn {
    font-size: 0.7rem;
    padding: 3px 6px;
  }
}
</style>


